<!--
  Milestones — an editorial scrollytelling timeline of historic "firsts"
  in powerlifting. Data: GET /milestones (server-side ordered ASC by
  meet date). Rendered as a single-column long-form document — no
  cards, no alternating-side layout, no emoji. The page treats each
  milestone as a chapter:

    - Era break (full-bleed chapter heading, only between eras)
    - Year as a giant ghost numeral behind the scene
    - The threshold value at ~200px in Instrument Serif (the part
      anyone will remember)
    - Lift-type letter (S / B / D / Σ / Δ) drawn large on the spine
    - Story prose at editorial reading width
    - Lifter + meet + federation + equipment metadata, footer

  After every appearance of a lifter who's already appeared, we surface
  a pull quote — the page "notices" repeat performers (e.g. April
  Mathis crossing 3 milestones 2009-2012).

  Scroll-linked motion via `animation-timeline: view()` where supported
  (Chrome 115+, ~85% of users). IntersectionObserver fallback for
  Safari / older Chrome.
-->
<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import type { Milestone } from '../api';
import type { MilestoneWithImage } from '../data/milestones';
import { MILESTONES } from '../data/milestones';
import LoadingState from '../components/LoadingState.vue';

// Milestones are hardcoded (see src/data/milestones.ts) — historic firsts
// don't change, so there's no fetch, no loading state, and no way for the
// page to 500. `loading`/`error` are kept only so the template's guards
// still type-check; they never flip.
const milestones = ref<MilestoneWithImage[]>(MILESTONES);
const loading = ref(false);
const error = ref<string | null>(null);
const activeYear = ref<string | null>(null);

let observer: IntersectionObserver | null = null;
let scrubberObserver: IntersectionObserver | null = null;

onMounted(async () => {
  // Data is already present (hardcoded). Wait one tick so the scene DOM is
  // painted before we attach the reveal / scrubber observers.
  await new Promise((r) => setTimeout(r, 50));
  observer = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        if (e.isIntersecting) {
          e.target.classList.add('visible');
          observer?.unobserve(e.target);
        }
      }
    },
    { threshold: 0.15, rootMargin: '0px 0px -10% 0px' },
  );
  document.querySelectorAll('.ms-scene, .ms-era-break, .ms-pull-quote').forEach((el) => observer!.observe(el));

  // Separate observer drives the sticky year scrubber on the right.
  // Fires when a scene's title block crosses the viewport's vertical
  // center → that's the "current" year.
  scrubberObserver = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        if (e.isIntersecting) {
          activeYear.value = (e.target as HTMLElement).dataset.year ?? null;
        }
      }
    },
    { rootMargin: '-45% 0px -45% 0px', threshold: 0 },
  );
  document.querySelectorAll('.ms-scene[data-year]').forEach((el) => scrubberObserver!.observe(el));
});

onUnmounted(() => { observer?.disconnect(); scrubberObserver?.disconnect(); });

// ─── Era model ──────────────────────────────────────────────────────
// Eras are chapter marks. The first milestone whose year crosses an
// era boundary triggers a full-bleed chapter heading before it. The
// labels are intentionally bold — these are read as titles, not
// throwaway taxonomy.
const ERAS = [
  { id: 'I',   minYear: 0,    maxYear: 1979, name: 'The Wraps Era',          subtitle: 'Before "raw" had a name, every great lift was wrapped, untested, and contested in basement gyms.' },
  { id: 'II',  minYear: 1980, maxYear: 2007, name: 'The Equipped Decades',    subtitle: 'The suit era took over the record books. The names below cleared raw thresholds in spite of it.' },
  { id: 'III', minYear: 2008, maxYear: 9999, name: 'The Raw Revolution',     subtitle: 'IPF rules formally embraced raw competition in 2012. The ceilings fell across every weight class.' },
] as const;

function eraFor(iso: string): (typeof ERAS)[number] {
  const y = Number(iso.slice(0, 4));
  return ERAS.find((e) => y >= e.minYear && y <= e.maxYear)!;
}

// ─── Scene list ────────────────────────────────────────────────────
// Flat sequence of renderables: era-break markers + milestone scenes
// + pull-quote callouts when a lifter repeats. Each tagged with a
// kind so the template can branch.
type Scene =
  | { kind: 'era'; id: string; era: typeof ERAS[number] }
  | { kind: 'milestone'; id: string; m: MilestoneWithImage }
  | { kind: 'quote'; id: string; lifter: string; count: number; range: string };

const scenes = computed<Scene[]>(() => {
  const out: Scene[] = [];
  let lastEraId: string | null = null;
  const lifterAppearances = new Map<string, MilestoneWithImage[]>();
  for (const m of milestones.value) {
    const e = eraFor(m.meet.meet_date);
    if (e.id !== lastEraId) {
      out.push({ kind: 'era', id: `era-${e.id}`, era: e });
      lastEraId = e.id;
    }
    out.push({ kind: 'milestone', id: `m-${m.id}`, m });
    // Track repeat performers — the page calls them out after their
    // last appearance with a pull quote.
    const arr = lifterAppearances.get(m.lifter.name) ?? [];
    arr.push(m);
    lifterAppearances.set(m.lifter.name, arr);
  }
  // Insert pull quotes after the last appearance of each multi-record lifter.
  const final: Scene[] = [];
  const printed = new Set<string>();
  for (let i = 0; i < out.length; i++) {
    final.push(out[i]);
    if (out[i].kind === 'milestone') {
      const m = (out[i] as Extract<Scene, { kind: 'milestone' }>).m;
      const appearances = lifterAppearances.get(m.lifter.name) ?? [];
      const isLast = appearances[appearances.length - 1]?.id === m.id;
      if (isLast && appearances.length >= 3 && !printed.has(m.lifter.name)) {
        const years = appearances.map((a) => a.meet.meet_date.slice(0, 4));
        const range = years[0] === years[years.length - 1]
          ? years[0]
          : `${years[0]}-${years[years.length - 1]}`;
        final.push({
          kind: 'quote',
          id: `q-${m.lifter.name}`,
          lifter: m.lifter.name,
          count: appearances.length,
          range,
        });
        printed.add(m.lifter.name);
      }
    }
  }
  return final;
});

// ─── Formatting ────────────────────────────────────────────────────
function formatDate(iso: string): string {
  return new Date(iso + 'T12:00:00Z').toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC',
  });
}

// Lift-letter glyph on the spine node. Σ = total (sum), Δ = Dots
// (deltas / ratio scoring). One-character marker reads as editorial
// chapter ornament rather than UI affordance.
function liftGlyph(m: Milestone): string {
  switch (m.threshold_field) {
    case 'total_kg':         return 'Σ';
    case 'best3_squat_kg':   return 'S';
    case 'best3_bench_kg':   return 'B';
    case 'best3_deadlift_kg':return 'D';
    case 'dots':             return 'Δ';
  }
  return '·';
}

// The big number — split into integer + unit so we can render the
// unit at a smaller scale (type-as-image with proper hierarchy).
function bigNumber(m: Milestone): { value: string; unit: string } {
  const f = m.threshold_field;
  if (f === 'total_kg')          return { value: (m.result.total    ?? 0).toFixed(0), unit: 'kg' };
  if (f === 'best3_squat_kg')    return { value: (m.result.squat    ?? 0).toFixed(1), unit: 'kg' };
  if (f === 'best3_bench_kg')    return { value: (m.result.bench    ?? 0).toFixed(1), unit: 'kg' };
  if (f === 'best3_deadlift_kg') return { value: (m.result.deadlift ?? 0).toFixed(1), unit: 'kg' };
  if (f === 'dots')              return { value: (m.result.dots     ?? 0).toFixed(2), unit: 'Dots' };
  return { value: '', unit: '' };
}

// For total milestones, surface the S/B/D breakdown as a tertiary
// line under the big number. Single-lift milestones get bodyweight +
// weight class instead. Either way: ONE supporting line, not a grid.
function supportLine(m: Milestone): string | null {
  if (m.threshold_field === 'total_kg' && m.result.squat != null && m.result.bench != null && m.result.deadlift != null) {
    return `${m.result.squat.toFixed(0)} squat · ${m.result.bench.toFixed(0)} bench · ${m.result.deadlift.toFixed(0)} deadlift`;
  }
  const bw = m.result.bodyweight_kg != null ? `${m.result.bodyweight_kg.toFixed(1)} kg bodyweight` : null;
  const wc = m.result.weight_class_kg ? `${m.result.weight_class_kg} kg class` : null;
  return [wc, bw].filter(Boolean).join(' · ') || null;
}

// Scrubber list — one entry per UNIQUE year. Milestones are ordered strictly
// by date, so the first milestone we see for a year is its topmost one; the
// scrubber link jumps there. Prevents the same year (1969, 1972, 2010, 2018…)
// repeating down the rail when several milestones fall in one year.
const scrubberItems = computed(() => {
  const seen = new Set<string>();
  const out: { year: string; id: string }[] = [];
  for (const m of milestones.value) {
    const year = m.meet.meet_date.slice(0, 4);
    if (seen.has(year)) continue;
    seen.add(year);
    out.push({ year, id: `m-${m.id}` });
  }
  return out;
});

// Hero meta line — derived from the loaded data so it can never
// contradict what's actually on the page. Was hardcoded
// ("Eleven entries · 1965 to 2014"), which would silently go stale the
// moment a new milestone is added or the corpus's earliest/latest first
// shifts.
const heroMeta = computed(() => {
  const ms = milestones.value;
  if (!ms.length) return '';
  const years = ms.map((m) => Number(m.meet.meet_date.slice(0, 4)));
  const min = Math.min(...years);
  const max = Math.max(...years);
  const span = min === max ? `${min}` : `${min} to ${max}`;
  return `${ms.length} entries · ${span} · Updated nightly`;
});

// Default the scrubber to the FIRST year as soon as data lands. Without
// this, the scrubber renders with every item in its dim "inactive"
// state until the IntersectionObserver fires on the first scene —
// which doesn't happen until the user scrolls past the hero. Visually
// the scrubber appears to "switch on" mid-page; users read that as
// broken. Defaulting fixes the appearance.
watch(milestones, (ms) => {
  if (ms.length > 0 && activeYear.value === null) {
    activeYear.value = ms[0].meet.meet_date.slice(0, 4);
  }
});
</script>

<template>
  <div class="milestones-page">
    <!-- Hero — asymmetric, lede anchored bottom-left. Title gets the
         air. No drifting blobs; just a single hairline rule + grain. -->
    <header class="ms-hero">
      <div class="ms-hero-inner">
        <p class="ms-eyebrow">A Powerlifting Timeline</p>
        <h1 class="ms-title">Powerlifting's<br/><span class="ms-title-em">firsts</span></h1>
      </div>
      <div class="ms-hero-lede">
        <p>
          The first man to total 1,000 kilos without a suit. The first woman
          to deadlift 250. Each entry below is the earliest performance in
          the OpenPowerlifting dataset to cross a mythologized number.
          Ordered strictly by date, every one a chapter the sport built on.
        </p>
        <p v-if="heroMeta" class="ms-hero-meta">{{ heroMeta }}</p>
      </div>
    </header>

    <LoadingState v-if="loading">Loading the timeline…</LoadingState>
    <div v-else-if="error" class="ms-err">⚠ {{ error }}</div>

    <!-- Body -->
    <main v-else class="ms-body">
      <!-- Sticky year scrubber, right edge of viewport.
           Teleported to <body> so it can't be reparented by an ancestor's
           transform/filter (which would turn position:fixed into
           position:absolute and pin it inside the timeline column). -->
      <Teleport to="body">
        <nav class="ms-scrubber" aria-label="Timeline years">
          <a
            v-for="item in scrubberItems"
            :key="item.id"
            :href="`#${item.id}`"
            :class="{ active: activeYear === item.year }"
          >{{ item.year }}</a>
        </nav>
      </Teleport>

      <template v-for="scene in scenes" :key="scene.id">
        <!-- ── Era chapter heading ── -->
        <section
          v-if="scene.kind === 'era'"
          class="ms-era-break"
        >
          <p class="ms-era-num">Chapter {{ scene.era.id }}</p>
          <h2 class="ms-era-name">{{ scene.era.name }}</h2>
          <p class="ms-era-range">{{ scene.era.minYear === 0 ? 'Up to ' + scene.era.maxYear : scene.era.minYear + ' to ' + (scene.era.maxYear === 9999 ? 'present' : scene.era.maxYear) }}</p>
          <p class="ms-era-blurb">{{ scene.era.subtitle }}</p>
        </section>

        <!-- ── Milestone scene ── -->
        <section
          v-else-if="scene.kind === 'milestone'"
          class="ms-scene"
          :class="`ms-equip-band-${scene.m.result.equipment.toLowerCase().replace(/\s|-/g, '')}`"
          :data-year="scene.m.meet.meet_date.slice(0, 4)"
          :id="scene.id"
        >
          <!-- Year as ghost backdrop numeral -->
          <div class="ms-year-back" aria-hidden="true">{{ scene.m.meet.meet_date.slice(0, 4) }}</div>

          <div class="ms-scene-inner">
            <!-- Glyph in the brand serif (Σ / S / B / D / Δ) -->
            <div class="ms-glyph" aria-hidden="true">{{ liftGlyph(scene.m) }}</div>

            <p class="ms-scene-kicker">{{ formatDate(scene.m.meet.meet_date) }}</p>

            <h3 class="ms-scene-title">{{ scene.m.title }}</h3>

            <div class="ms-bignum">
              <span class="ms-bignum-val">{{ bigNumber(scene.m).value }}</span>
              <span class="ms-bignum-unit">{{ bigNumber(scene.m).unit }}</span>
            </div>
            <p v-if="supportLine(scene.m)" class="ms-bignum-support">{{ supportLine(scene.m) }}</p>

            <p class="ms-scene-story">{{ scene.m.story }}</p>

            <!-- Portrait, only for milestones with an open-license photo. -->
            <figure v-if="scene.m.image" class="ms-portrait">
              <img :src="scene.m.image.src" :alt="`${scene.m.lifter.name}, powerlifter`" loading="lazy" />
              <figcaption>
                <a :href="scene.m.image.sourceUrl" target="_blank" rel="noopener">
                  <template v-if="scene.m.image.credit">{{ scene.m.image.credit }} · </template>{{ scene.m.image.license }}
                </a>
              </figcaption>
            </figure>

            <footer class="ms-scene-foot">
              <div class="ms-scene-attrib">
                <a :href="`/lifter?id=${scene.m.lifter.lifter_id}`" class="ms-lifter">{{ scene.m.lifter.name }}</a>
                <span class="ms-meta-sep">at</span>
                <a :href="`/meets?id=${scene.m.meet.meet_id}`" class="ms-meet">{{ scene.m.meet.meet_name || `Meet #${scene.m.meet.meet_id}` }}</a>
              </div>
              <div class="ms-scene-tags">
                <span v-if="scene.m.meet.federation" class="ms-tag">{{ scene.m.meet.federation }}</span>
                <span class="ms-tag" :class="`ms-tag-equip-${scene.m.result.equipment.toLowerCase().replace(/\s|-/g, '')}`">{{ scene.m.result.equipment }}</span>
                <span v-if="scene.m.result.weight_class_kg" class="ms-tag">{{ scene.m.result.weight_class_kg }} kg</span>
              </div>
            </footer>
          </div>
        </section>

        <!-- ── Pull quote (page notices a repeat performer) ── -->
        <aside
          v-else-if="scene.kind === 'quote'"
          class="ms-pull-quote"
        >
          <p>
            For {{ scene.count }} years between {{ scene.range }},
            <em>{{ scene.lifter }}</em>
            crossed {{ scene.count }} thresholds in this list. A single name
            running through the era like a thread.
          </p>
          <footer>A pattern in the data</footer>
        </aside>
      </template>

      <section class="ms-coda">
        <p class="ms-coda-line">More firsts are coming.</p>
        <p class="ms-coda-sub">As today's lifters become tomorrow's pioneers, and as new thresholds get drawn in chalk on the platform, this page updates itself.</p>
      </section>
    </main>
  </div>
</template>

<style scoped>
/* ───── Page-level grain + variables ──────────────────────────────── */
.milestones-page {
  position: relative;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px 120px;
}
/* 4% noise overlay — fixed so it doesn't scroll, gives the page the
   texture of newsprint instead of pure digital void. */
.milestones-page::after {
  content: '';
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 100;
  opacity: 0.045;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'><filter id='n'><feTurbulence baseFrequency='0.92' numOctaves='3' seed='4'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.55 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>");
}

/* ───── Hero ──────────────────────────────────────────────────────── */
.ms-hero {
  position: relative;
  min-height: 86vh;
  display: grid;
  grid-template-rows: 1fr auto;
  margin: 0 -24px 40px;
  padding: 80px 24px 60px;
  border-bottom: 1px solid var(--border);
  overflow: hidden;
}
.ms-hero-inner {
  align-self: end;
  max-width: 1100px;
  padding-bottom: 40px;
}
.ms-eyebrow {
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.32em;
  color: var(--text-dim);
  margin: 0 0 24px;
}
.ms-title {
  font-family: 'Instrument Serif', Georgia, serif;
  font-size: clamp(72px, 14vw, 200px);
  font-weight: 400;
  line-height: 0.88;
  margin: 0;
  letter-spacing: -0.04em;
  color: var(--text);
}
.ms-title-em {
  font-style: italic;
  color: #c4f04a;
}
.ms-hero-lede {
  align-self: end;
  max-width: 460px;
  justify-self: end;
  padding: 0 0 0 24px;
  border-left: 1px solid var(--border);
}
.ms-hero-lede p {
  font-family: 'Instrument Serif', Georgia, serif;
  font-size: clamp(15px, 1.2vw, 18px);
  font-style: italic;
  line-height: 1.55;
  color: var(--text-dim);
  margin: 0 0 16px;
}
.ms-hero-meta {
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10.5px !important;
  font-style: normal !important;
  text-transform: uppercase;
  letter-spacing: 0.2em;
  color: var(--text-dim);
  margin-top: 24px !important;
}

/* ───── Scrubber (right-edge sticky year navigation) ──────────────── */
/* z-index: 200 sits ABOVE the page grain overlay (z-100) and the
   year-back ghost numerals (z-0). position: fixed → relative to the
   viewport, NOT the .milestones-page max-width wrapper. */
.ms-scrubber {
  position: fixed;
  right: 24px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 11px;
  letter-spacing: 0.08em;
  z-index: 200;
  padding: 14px 10px 14px 14px;
  background: rgba(14, 17, 22, 0.78);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid var(--border);
  border-right: none;
  border-radius: 6px 0 0 6px;
  box-shadow: -2px 0 20px rgba(0, 0, 0, 0.35);
}
.ms-scrubber a {
  display: flex;
  align-items: center;
  gap: 8px;
  /* Brighter than --text-dim so each year reads at a glance, including
     when no year is currently "active" (e.g. while the user is still
     in the hero, before any scene crosses the viewport center). */
  color: rgba(255, 255, 255, 0.55);
  text-decoration: none;
  transition: color 0.25s, letter-spacing 0.25s;
  white-space: nowrap;
  padding: 1px 0;
}
.ms-scrubber a::before {
  content: '';
  display: inline-block;
  width: 14px;
  height: 1px;
  background: currentColor;
  transition: width 0.25s, background 0.25s, opacity 0.25s;
  opacity: 0.6;
}
.ms-scrubber a:hover { color: var(--text); }
.ms-scrubber a.active {
  color: #c4f04a;
  font-weight: 700;
  letter-spacing: 0.15em;
}
.ms-scrubber a.active::before {
  width: 30px;
  background: #c4f04a;
  opacity: 1;
}

/* ───── Body container ────────────────────────────────────────────── */
.ms-body { position: relative; }

/* ───── Era chapter break ─────────────────────────────────────────── */
.ms-era-break {
  min-height: 80vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 80px 0 80px;
  border-top: 1px solid var(--border);
  margin: 60px 0 40px;
  max-width: 720px;
  opacity: 0;
  transform: translateY(30px);
  transition: opacity 0.9s ease-out, transform 0.9s cubic-bezier(0.2, 0.9, 0.3, 1);
}
.ms-era-break.visible {
  opacity: 1;
  transform: translateY(0);
}
.ms-era-num {
  font-family: 'Instrument Serif', serif;
  font-style: italic;
  font-size: clamp(22px, 2.5vw, 32px);
  color: #f78166;
  margin: 0 0 12px;
}
.ms-era-name {
  font-family: 'Instrument Serif', serif;
  font-size: clamp(56px, 9vw, 128px);
  font-weight: 400;
  line-height: 0.95;
  letter-spacing: -0.03em;
  margin: 0 0 24px;
  color: var(--text);
}
.ms-era-range {
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 12px;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: var(--text-dim);
  margin: 0 0 28px;
}
.ms-era-blurb {
  font-family: 'Instrument Serif', serif;
  font-style: italic;
  font-size: clamp(18px, 1.6vw, 22px);
  line-height: 1.5;
  color: var(--text-dim);
  max-width: 540px;
  margin: 0;
}

/* ───── Milestone scene ───────────────────────────────────────────── */
.ms-scene {
  position: relative;
  padding: 100px 0 80px;
  border-top: 1px solid var(--border);
  opacity: 0;
  transform: translateY(40px);
  transition: opacity 0.8s ease-out, transform 0.8s cubic-bezier(0.2, 0.9, 0.3, 1);
}
.ms-scene.visible { opacity: 1; transform: translateY(0); }

/* The ghost year backdrop — the load-bearing piece of editorial
   weight. Outlined stencil numeral that bleeds off the right edge. */
.ms-year-back {
  position: absolute;
  top: 50%;
  right: -0.04em;
  transform: translateY(-50%);
  font-family: 'Instrument Serif', serif;
  font-size: clamp(220px, 34vw, 540px);
  line-height: 0.85;
  font-weight: 400;
  letter-spacing: -0.06em;
  color: transparent;
  -webkit-text-stroke: 1px rgba(255, 255, 255, 0.06);
  pointer-events: none;
  z-index: 0;
  user-select: none;
  white-space: nowrap;
}

.ms-scene-inner {
  position: relative;
  z-index: 1;
  max-width: 720px;
}

.ms-glyph {
  font-family: 'Instrument Serif', serif;
  font-style: italic;
  font-size: clamp(60px, 7vw, 96px);
  line-height: 0.85;
  color: var(--text-dim);
  margin: 0 0 32px;
  opacity: 0.6;
}
.ms-equip-band-raw   .ms-glyph { color: #c4f04a; opacity: 0.85; }
.ms-equip-band-wraps .ms-glyph { color: #f78166; opacity: 0.85; }

.ms-scene-kicker {
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 11px;
  letter-spacing: 0.25em;
  text-transform: uppercase;
  color: var(--text-dim);
  margin: 0 0 18px;
}
.ms-scene-title {
  font-family: 'Instrument Serif', serif;
  font-size: clamp(28px, 3.4vw, 44px);
  font-weight: 400;
  line-height: 1.1;
  letter-spacing: -0.015em;
  color: var(--text);
  margin: 0 0 40px;
  max-width: 640px;
}

/* The number — the thing anyone will remember. Trust the type. */
.ms-bignum {
  display: flex;
  align-items: baseline;
  gap: 16px;
  margin: 0 0 8px;
}
.ms-bignum-val {
  font-family: 'Instrument Serif', serif;
  font-weight: 400;
  font-size: clamp(120px, 18vw, 240px);
  line-height: 0.82;
  letter-spacing: -0.05em;
  font-variant-numeric: lining-nums tabular-nums;
  color: var(--text);
}
.ms-equip-band-raw   .ms-bignum-val { color: #c4f04a; }
.ms-equip-band-wraps .ms-bignum-val { color: #f78166; }

.ms-bignum-unit {
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: clamp(14px, 1.4vw, 18px);
  font-weight: 600;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--text-dim);
  align-self: flex-end;
  padding-bottom: clamp(18px, 3vw, 36px);
}
.ms-bignum-support {
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 12px;
  letter-spacing: 0.06em;
  color: var(--text-dim);
  margin: 0 0 36px;
}

.ms-scene-story {
  font-family: 'Instrument Serif', serif;
  font-size: clamp(18px, 1.5vw, 21px);
  line-height: 1.55;
  color: var(--text);
  margin: 0 0 36px;
  max-width: 600px;
}

/* Lifter portrait — only present on milestones with an open-license photo. */
.ms-portrait {
  margin: 0 0 34px;
  width: fit-content;
  max-width: 260px;
}
.ms-portrait img {
  display: block;
  width: 100%;
  height: auto;
  max-height: 320px;
  object-fit: cover;
  border-radius: 12px;
  border: 1px solid var(--border);
  filter: saturate(0.92);
}
.ms-portrait figcaption {
  margin-top: 7px;
  font-family: var(--font-mono);
  font-size: 10px;
  letter-spacing: 0.04em;
  color: var(--text-mute);
}
.ms-portrait figcaption a { color: var(--text-mute); text-decoration: none; }
.ms-portrait figcaption a:hover { color: var(--text-dim); text-decoration: underline; }

.ms-scene-foot {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  align-items: flex-end;
  justify-content: space-between;
  padding-top: 24px;
  border-top: 1px solid var(--border);
  max-width: 720px;
}
.ms-scene-attrib {
  font-family: 'Instrument Serif', serif;
  font-size: clamp(16px, 1.4vw, 19px);
  line-height: 1.3;
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 10px;
}
.ms-lifter, .ms-meet {
  text-decoration: none;
  border-bottom: 1px solid transparent;
  transition: border-color 0.2s, color 0.2s;
}
.ms-lifter {
  color: var(--text);
  font-weight: 500;
}
.ms-lifter:hover { border-bottom-color: #c4f04a; }
.ms-meet {
  color: var(--text-dim);
  font-style: italic;
}
.ms-meet:hover { color: var(--text); border-bottom-color: var(--text-dim); }
.ms-meta-sep {
  color: var(--text-dim);
  font-style: italic;
  font-size: 0.85em;
}

.ms-scene-tags {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.ms-tag {
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: var(--text-dim);
  padding: 4px 10px;
  border: 1px solid var(--border);
  border-radius: 0;
}
.ms-tag-equip-raw   { color: #c4f04a; border-color: rgba(196, 240, 74, 0.4); }
.ms-tag-equip-wraps { color: #f78166; border-color: rgba(247, 129, 102, 0.4); }

/* ───── Pull quote (page-notices-a-pattern callout) ───────────────── */
.ms-pull-quote {
  max-width: 720px;
  margin: 100px 0 60px;
  padding: 24px 0 24px 36px;
  border-left: 2px solid #c4f04a;
  opacity: 0;
  transform: translateX(-20px);
  transition: opacity 0.9s ease-out, transform 0.9s cubic-bezier(0.2, 0.9, 0.3, 1);
}
.ms-pull-quote.visible {
  opacity: 1;
  transform: translateX(0);
}
.ms-pull-quote p {
  font-family: 'Instrument Serif', serif;
  font-size: clamp(28px, 3vw, 40px);
  font-style: italic;
  line-height: 1.25;
  color: var(--text);
  margin: 0 0 16px;
  letter-spacing: -0.01em;
}
.ms-pull-quote p em {
  font-style: normal;
  color: #c4f04a;
  background: linear-gradient(180deg, transparent 60%, rgba(196, 240, 74, 0.18) 60%);
  padding: 0 4px;
}
.ms-pull-quote footer {
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 11px;
  letter-spacing: 0.25em;
  text-transform: uppercase;
  color: var(--text-dim);
}

/* ───── Coda ──────────────────────────────────────────────────────── */
.ms-coda {
  margin-top: 140px;
  padding-top: 60px;
  border-top: 1px solid var(--border);
  max-width: 540px;
}
.ms-coda-line {
  font-family: 'Instrument Serif', serif;
  font-style: italic;
  font-size: clamp(28px, 3vw, 38px);
  line-height: 1.2;
  margin: 0 0 16px;
  color: var(--text);
}
.ms-coda-sub {
  font-family: 'Instrument Serif', serif;
  font-size: 16px;
  line-height: 1.5;
  color: var(--text-dim);
  margin: 0;
}

.ms-err {
  text-align: center;
  padding: 80px;
  color: #f87171;
  font-family: 'Instrument Serif', serif;
  font-size: 20px;
}

/* ───── Scroll-linked motion (modern browsers) ────────────────────── */
/* When supported, the BIG number scales/letterspaces into position
   as you scroll the scene into view — feels deliberate rather than
   one-shot. Falls back gracefully to the IntersectionObserver
   `.visible` reveal in Safari and older Chromium. */
@supports (animation-timeline: view()) {
  .ms-bignum-val {
    animation: number-arrive linear both;
    animation-timeline: view();
    animation-range: entry 0% cover 50%;
  }
  @keyframes number-arrive {
    from {
      letter-spacing: 0em;
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      letter-spacing: -0.05em;
      opacity: 1;
      transform: translateY(0);
    }
  }

  .ms-year-back {
    animation: ghost-drift linear both;
    animation-timeline: view();
    animation-range: entry 0% exit 100%;
  }
  @keyframes ghost-drift {
    from { transform: translateY(-50%) translateX(80px); opacity: 0.0; }
    50%  { transform: translateY(-50%) translateX(0);    opacity: 1.0; }
    to   { transform: translateY(-50%) translateX(-80px); opacity: 0.0; }
  }
}

/* ───── Mobile ────────────────────────────────────────────────────── */
@media (max-width: 880px) {
  .ms-hero {
    grid-template-rows: auto auto;
    min-height: auto;
    padding: 60px 24px 40px;
  }
  .ms-hero-inner { padding-bottom: 24px; }
  .ms-hero-lede {
    justify-self: start;
    border-left: none;
    padding: 0;
    border-top: 1px solid var(--border);
    padding-top: 24px;
    max-width: 100%;
  }
  .ms-year-back { font-size: clamp(140px, 40vw, 280px); right: -0.1em; }
  .ms-scene { padding: 60px 0 50px; }
  .ms-era-break { min-height: 50vh; padding: 50px 0; }
  .ms-scene-foot { flex-direction: column; align-items: flex-start; gap: 16px; }
  .ms-pull-quote { padding-left: 20px; margin: 60px 0 40px; }
  .milestones-page { padding-bottom: 80px; }  /* room for the fixed scrubber */

  /* Mobile scrubber: horizontal strip pinned to the bottom of the
     viewport. Snap-scrolls so each year tap-target self-aligns. The
     backdrop-blur keeps years readable as the content scrolls behind.
     box-shadow above so the strip reads as a distinct surface against
     the page even when content directly behind it is empty (e.g. at
     the very top of the page). */
  .ms-scrubber {
    flex-direction: row;
    position: fixed;
    bottom: 0; left: 0; right: 0;
    top: auto;
    transform: none;
    padding: 12px 16px calc(12px + env(safe-area-inset-bottom));
    gap: 22px;
    background: rgba(14, 17, 22, 0.92);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    border-top: 1px solid var(--border);
    border-left: none;
    border-right: none;
    border-bottom: none;
    border-radius: 0;
    box-shadow: 0 -8px 24px rgba(0, 0, 0, 0.4);
    overflow-x: auto;
    overflow-y: hidden;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    -ms-overflow-style: none;
    scrollbar-width: none;
    font-size: 12px;
    /* Pin above iOS keyboard / browser UI overlay */
    z-index: 201;
  }
  .ms-scrubber::-webkit-scrollbar { display: none; }
  .ms-scrubber a {
    scroll-snap-align: center;
    padding: 6px 2px;
    flex-shrink: 0;
    color: rgba(255, 255, 255, 0.6);
  }
  .ms-scrubber a::before { display: none; }
  .ms-scrubber a.active {
    color: #c4f04a;
    border-bottom: 2px solid #c4f04a;
    padding-bottom: 4px;
  }
}
</style>
